/**
 * An issuance whose outcome nobody knows, kept safely until somebody knows it.
 *
 * A request that reached the server and whose answer did not come back may or may not have created
 * a document. Telling the operator «Oluşturulamadı» is the sentence that makes them create a second
 * one, so the intent has to outlive the window — and, as the independent review pointed out, it has
 * to outlive the SCREEN too: the intent lived in React state, so leaving the register or pressing
 * reload silently threw it away and the next attempt was a brand-new key for work the server may
 * already have done.
 *
 * What is kept and what is not:
 *
 *   - kept: the idempotency key, the template and the RELEASE the first attempt actually used, the
 *     validated claims, the recipient's address, the dates, the language and the send flag. The
 *     release id is part of it because a retry has to repeat the first attempt, not compose a new
 *     one: if a colleague published a new version while the answer was missing, re-reading «the
 *     current release» would send different content under the same key.
 *   - never kept: any access, refresh or claim token, any password or MFA secret, any session. None
 *     of them is an input to this command, and none of them is read here.
 *
 * Where and for how long: `sessionStorage`, which belongs to one tab and is gone when that tab is
 * closed — so a reload keeps it and a shared machine does not inherit it. It carries a recipient's
 * name and address, which is ordinary personal data and must not sit around indefinitely, so it is
 * also stamped and refused once stale, cleared the moment the intent completes or is abandoned, and
 * cleared on sign-out. It is bound to the user who started it: another account, on the same tab,
 * does not see it and cannot resume it.
 */

const KEY = 'hpw.v07.pending-issue';
/**
 * What is left when the intent itself cannot be used, and why.
 *
 * An intent that is past its window or that does not read back as the request it claims to be is
 * UNUSABLE — it must not be retried, because retrying a reconstruction under the same key is the
 * one thing the key exists to prevent. But «unusable» is emphatically not «there was never a
 * request»: somebody pressed «Oluştur», the server may well have made the document, and nobody
 * knows. Deleting the record and saying nothing is how that fact disappeared.
 *
 * So the personal data goes at the twelve-hour mark, exactly as before, and this stays in its
 * place: the key, the template and when it started. No name, no address, no claim, no token — a
 * breadcrumb for the operator and for the register, not a copy of the request. It lives in the same
 * `sessionStorage` and dies with the tab, so it introduces no retention question of its own.
 */
const TRACE_KEY = 'hpw.v07.pending-issue.trace';
/** Long enough to survive a reload, a coffee and a network outage; not long enough to linger. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface PendingIssueIntent {
  idempotencyKey: string;
  templateId: string;
  /** The release the FIRST attempt was composed against — a retry repeats it rather than re-picking. */
  releaseId: string;
  claims: Record<string, string>;
  email: string;
  issueDate: string;
  expiresAt: string;
  lang: 'tr' | 'en';
  sendEmail: boolean;
  /** Who started it. Not a secret and not a token: the internal user id the session already shows. */
  ownerKey: string;
  /** When it was FIRST sent — never restamped by a retry, or the window would never close. */
  startedAt: string;
}

/** Everything that is kept about an unusable intent. Deliberately carries no personal data. */
export interface PendingIssueTrace {
  idempotencyKey: string;
  templateId: string;
  startedAt: string;
  ownerKey: string;
  reason: 'expired' | 'unreadable';
}

/**
 * What the storage has to say about an unfinished issuance — three answers, not two.
 *
 * The screen used to receive `null` for «nothing here», «this record is damaged» and «this record
 * is too old to hold somebody's address any longer», and treated all three as an empty desk: the
 * next click opened a clean form and minted a fresh key, as if the earlier request had never been
 * made. The first case is genuinely an empty desk. The other two are an outcome nobody knows.
 */
export type PendingIntentRead =
  | { kind: 'none' }
  | { kind: 'intent'; intent: PendingIssueIntent }
  | { kind: 'unusable'; trace: PendingIssueTrace | null };

const store = (): Storage | null => {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    /* A browser that refuses storage is not a reason to fail an issuance; it only loses the resume. */
    return null;
  }
};

/**
 * Keeps the intent for a reload, and says whether it really managed to.
 *
 * A storage that is full, blocked or disabled throws, and swallowing that told the operator they
 * could reload when they could not: the promise a screen makes has to be the one it can keep. The
 * in-memory copy is unaffected either way, so the window and the retry still work — what changes is
 * only whether the screen may claim it will survive a refresh.
 */
export function writePendingIntent(intent: PendingIssueIntent): boolean {
  const s = store();
  if (!s) return false;
  try {
    s.setItem(KEY, JSON.stringify(intent));
    return true;
  } catch {
    return false;
  }
}

export function clearPendingIntent(): void {
  try {
    store()?.removeItem(KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Forget an intent, but not the fact that there WAS one.
 *
 * Used where the record has to go — it is damaged, or it is past the window this is allowed to hold
 * a name and an address for — and where the operator still has to be told that a request left this
 * tab and nobody knows what became of it.
 */
function discardIntent(trace: PendingIssueTrace | null): void {
  clearPendingIntent();
  if (trace === null) return;
  try {
    store()?.setItem(TRACE_KEY, JSON.stringify(trace));
  } catch {
    /* A storage that refuses the breadcrumb still lets the screen show it for this paint. */
  }
}

/** The operator has read it and said so; nothing about it is kept. */
export function acknowledgeUnusableIntent(): void {
  try {
    store()?.removeItem(TRACE_KEY);
  } catch {
    /* nothing to do */
  }
}

function readTrace(ownerKey: string): PendingIssueTrace | null {
  let raw: string | null = null;
  try {
    raw = store()?.getItem(TRACE_KEY) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    acknowledgeUnusableIntent();
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    acknowledgeUnusableIntent();
    return null;
  }
  const t = parsed as Record<string, unknown>;
  if (
    !str(t.idempotencyKey) ||
    !str(t.templateId) ||
    !str(t.startedAt) ||
    !str(t.ownerKey) ||
    (t.reason !== 'expired' && t.reason !== 'unreadable')
  ) {
    acknowledgeUnusableIntent();
    return null;
  }
  /* Somebody else's breadcrumb is not shown to this account, and is not deleted on their behalf. */
  if (t.ownerKey !== ownerKey) return null;
  return {
    idempotencyKey: t.idempotencyKey as string,
    templateId: t.templateId as string,
    startedAt: t.startedAt as string,
    ownerKey: t.ownerKey as string,
    reason: t.reason as 'expired' | 'unreadable',
  };
}

/** The non-personal part of a record, when enough of it reads back to be worth keeping. */
function traceOf(i: Record<string, unknown>, ownerKey: string, reason: PendingIssueTrace['reason']) {
  return str(i.idempotencyKey) && str(i.templateId) && str(i.startedAt)
    ? {
        idempotencyKey: i.idempotencyKey as string,
        templateId: i.templateId as string,
        startedAt: i.startedAt as string,
        ownerKey,
        reason,
      }
    : null;
}

/** A non-empty string, which is what every identifier on this record has to be. */
const str = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

/** Claims are a flat map of field key to string value — an array or a nested object is not one. */
function readClaims(v: unknown): Record<string, string> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const out: Record<string, string> = {};
  for (const [k, value] of Object.entries(v as Record<string, unknown>)) {
    /* A value of some other type is not coerced: the retry would then send a different payload from
       the one the first attempt sent, under the same idempotency key. */
    if (typeof value !== 'string') return null;
    out[k] = value;
  }
  return out;
}

/** `YYYY-MM-DD`, the only date shape this command takes. An empty expiry means «no expiry». */
const DATE_RX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The intent this user may resume, or nothing — and never a reconstruction of one.
 *
 * A record offered as «the same request» has to BE the same request. Every field that goes into
 * what the server fingerprints is required to be present and well formed: the key, the template and
 * the release it was composed against, the owner, the timestamp, the claims, the recipient's
 * address, both dates, the language and the send flag. Nothing is defaulted and nothing is coerced.
 *
 * That is stricter than it looks, and deliberately so. Filling a missing `sendEmail` with `true`, a
 * missing language with `tr` or a missing date with an empty string produces a DIFFERENT payload
 * under the SAME idempotency key — which is either a second document or a silent no-op, and in both
 * cases the operator was told they were retrying the thing they sent. A damaged record is therefore
 * unusable rather than repairable.
 *
 * And «unusable» is its own answer, not a synonym for «nothing here». Returning `null` for a
 * damaged or expired record made the screen open a clean form and mint a fresh key, which is the
 * silent second document this whole file exists to prevent — quieter than the bug it replaced,
 * because nobody was even told a request had gone missing. The three cases are now distinct, and
 * the two that are not «none» leave a breadcrumb carrying no personal data.
 */
export function readPendingIntent(ownerKey: string | null, now = Date.now()): PendingIntentRead {
  if (!ownerKey) return { kind: 'none' };
  const none = (): PendingIntentRead => {
    /* No live record — but a breadcrumb from one that was discarded is still an answer. */
    const trace = readTrace(ownerKey);
    return trace === null ? { kind: 'none' } : { kind: 'unusable', trace };
  };

  let raw: string | null = null;
  try {
    raw = store()?.getItem(KEY) ?? null;
  } catch {
    return none();
  }
  if (!raw) return none();

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    /* Unreadable bytes: nothing of it can be trusted, so not even a key can be salvaged. */
    discardIntent(null);
    return { kind: 'unusable', trace: null };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    discardIntent(null);
    return { kind: 'unusable', trace: null };
  }
  const i = parsed as Record<string, unknown>;

  /* Somebody else's work is refused but NOT deleted: it is theirs to resume, not ours to discard. */
  if (!str(i.ownerKey)) {
    discardIntent(null);
    return { kind: 'unusable', trace: null };
  }
  if (i.ownerKey !== ownerKey) return none();

  const claims = readClaims(i.claims);
  const wellFormed =
    str(i.idempotencyKey) &&
    str(i.templateId) &&
    str(i.releaseId) &&
    str(i.startedAt) &&
    claims !== null &&
    typeof i.email === 'string' &&
    typeof i.issueDate === 'string' &&
    DATE_RX.test(i.issueDate as string) &&
    typeof i.expiresAt === 'string' &&
    ((i.expiresAt as string) === '' || DATE_RX.test(i.expiresAt as string)) &&
    (i.lang === 'tr' || i.lang === 'en') &&
    typeof i.sendEmail === 'boolean';
  if (!wellFormed) {
    const trace = traceOf(i, ownerKey, 'unreadable');
    discardIntent(trace);
    return { kind: 'unusable', trace };
  }

  /*
   * Too old to offer, which is a statement about THIS tab and nothing else.
   *
   * It does not mean the server refused, and it does not mean no document exists — only that the
   * local copy is past the window this is allowed to hold somebody's name and address for. What
   * follows from it is «go and look at the register», never «make another one», and that sentence
   * now has somewhere to be said: the personal data goes and the breadcrumb stays.
   */
  const started = Date.parse(i.startedAt as string);
  if (!Number.isFinite(started) || now - started > MAX_AGE_MS) {
    const trace = traceOf(i, ownerKey, 'expired');
    discardIntent(trace);
    return { kind: 'unusable', trace };
  }

  return {
    kind: 'intent',
    intent: {
      idempotencyKey: i.idempotencyKey as string,
      templateId: i.templateId as string,
      releaseId: i.releaseId as string,
      claims: claims as Record<string, string>,
      email: i.email as string,
      issueDate: i.issueDate as string,
      expiresAt: i.expiresAt as string,
      lang: i.lang as 'tr' | 'en',
      sendEmail: i.sendEmail as boolean,
      ownerKey: i.ownerKey as string,
      startedAt: i.startedAt as string,
    },
  };
}

// ------------------------------------------------------ a design the server has not stored yet

/**
 * The seven steps' work, when the request that should have stored it did not.
 *
 * Creating a template and saving its design are two requests. When the second fails — refused, or
 * simply unanswered — the template EXISTS on the server carrying the backend's default draft, and
 * the only copy of the author's actual design is in the tab. It used to be held in a module
 * variable, which survives a route change and nothing else: pressing reload, which is exactly what
 * somebody does when a screen has just failed, threw the whole thing away.
 *
 * Kept per TEMPLATE, so two half-finished templates cannot overwrite each other's recovery, and
 * bound to the user who made it. Same storage discipline as the intent above: this tab only, no
 * token of any kind, stamped so it cannot linger, and dropped the moment the design is safely on
 * the server.
 */
const DRAFT_PREFIX = 'hpw.v07.pending-draft.';

export interface PendingDraft<T = unknown> {
  templateId: string;
  ownerKey: string;
  startedAt: string;
  /** The template record as the wizard composed it, under the id the SERVER gave it. */
  template: T;
}

const draftKey = (templateId: string): string => `${DRAFT_PREFIX}${templateId}`;

export function writePendingDraft<T>(templateId: string, ownerKey: string | null, template: T): boolean {
  if (!ownerKey) return false;
  const s = store();
  if (!s) return false;
  try {
    s.setItem(
      draftKey(templateId),
      JSON.stringify({ templateId, ownerKey, startedAt: new Date().toISOString(), template }),
    );
    return true;
  } catch {
    /* Quota or a privacy mode. The in-memory copy is still there for as long as the tab lives, and
       the caller is told the truth so it does not promise a reload it cannot survive. */
    return false;
  }
}

/**
 * Every unfinished design this account left in this tab, dropped.
 *
 * Sign-out is the moment the principle at the top of `ProductSurface` actually has to hold:
 * «nothing half-finished survives a sign-out, on a shared machine least of all». It used to hold
 * for the issuance intent and for nothing else, so an unsaved DESIGN — which can carry a
 * recipient's name in a text layer — stayed in the tab after its author had gone. Records are
 * cleared per owner rather than wholesale, so this can never discard somebody else's.
 */
export function clearPendingDraftsOf(ownerKey: string | null): void {
  if (!ownerKey) return;
  const s = store();
  if (!s) return;
  try {
    const doomed: string[] = [];
    for (let i = 0; i < s.length; i += 1) {
      const key = s.key(i);
      if (key === null || !key.startsWith(DRAFT_PREFIX)) continue;
      try {
        const parsed = JSON.parse(s.getItem(key) ?? 'null') as { ownerKey?: unknown } | null;
        if (parsed && parsed.ownerKey === ownerKey) doomed.push(key);
      } catch {
        /* Unreadable bytes under our own prefix are nobody's work; they go with the rest. */
        doomed.push(key);
      }
    }
    for (const key of doomed) s.removeItem(key);
  } catch {
    /* A storage that refuses to be read cannot be holding anything either. */
  }
}

export function clearPendingDraft(templateId: string): void {
  try {
    store()?.removeItem(draftKey(templateId));
  } catch {
    /* nothing to do */
  }
}

/**
 * The stored design AND when it was made.
 *
 * The stamp is not decoration: choosing between this copy and the server's draft is a question
 * about which one is newer, and a chooser that guesses from «is the other one empty?» will discard
 * whichever it guesses wrong about. `readPendingDraft` keeps its shape for callers that only want
 * the design.
 */
export function readPendingDraftRecord<T>(
  templateId: string,
  ownerKey: string | null,
  now = Date.now(),
): { template: T; startedAt: string } | null {
  if (!ownerKey || !templateId) return null;
  let raw: string | null = null;
  try {
    raw = store()?.getItem(draftKey(templateId)) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: PendingDraft<T> | null = null;
  try {
    parsed = JSON.parse(raw) as PendingDraft<T>;
  } catch {
    clearPendingDraft(templateId);
    return null;
  }
  if (!parsed || parsed.templateId !== templateId || !parsed.template) {
    clearPendingDraft(templateId);
    return null;
  }
  /* Somebody else's unfinished work is not offered, and is not deleted on their behalf either. */
  if (parsed.ownerKey !== ownerKey) return null;
  const started = Date.parse(parsed.startedAt);
  if (!Number.isFinite(started) || now - started > MAX_AGE_MS) {
    clearPendingDraft(templateId);
    return null;
  }
  return { template: parsed.template, startedAt: parsed.startedAt };
}

export function readPendingDraft<T>(templateId: string, ownerKey: string | null, now = Date.now()): T | null {
  if (!ownerKey || !templateId) return null;
  let raw: string | null = null;
  try {
    raw = store()?.getItem(draftKey(templateId)) ?? null;
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: PendingDraft<T> | null = null;
  try {
    parsed = JSON.parse(raw) as PendingDraft<T>;
  } catch {
    clearPendingDraft(templateId);
    return null;
  }
  if (!parsed || parsed.templateId !== templateId || !parsed.template) {
    clearPendingDraft(templateId);
    return null;
  }
  /* Somebody else's unfinished work is not offered, and is not deleted on their behalf either. */
  if (parsed.ownerKey !== ownerKey) return null;
  const started = Date.parse(parsed.startedAt);
  if (!Number.isFinite(started) || now - started > MAX_AGE_MS) {
    clearPendingDraft(templateId);
    return null;
  }
  return parsed.template;
}
